import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../components/ToastContainer';
import { useApiCall } from '../hooks/useApiCall';
import Button from '../components/Button';
import Input from '../components/Input';
import Logo from '../components/Logo';
import { api } from '../api/api';
import usePageTitle from '../utils/usePageTitle';

const ResetContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: var(--color-background);
  position: relative;
  overflow: hidden;
`;

const ResetCard = styled.div`
  background-color: var(--color-surface);
  border-radius: var(--border-radius-lg);
  padding: 32px;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.16);
  position: relative;
  z-index: 1;
`;

const ResetHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
`;

const ResetTitle = styled.h1`
  font-family: var(--font-family-main);
  font-size: 30px;
  font-weight: 500;
  color: var(--color-text);
  margin: 0;
`;

const ResetSubtitle = styled.p`
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const BackToLogin = styled(Link)`
  color: var(--color-text-secondary);
  font-size: 14px;
  text-align: center;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--color-primary);
  }
`;

const InfoBox = styled.div`
  background-color: rgba(96, 159, 148, 0.1);
  border: 1px solid rgba(96, 159, 148, 0.3);
  border-radius: var(--border-radius-md);
  padding: 16px;
  text-align: center;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 8px 0;
  line-height: 1.4;
`;

const BotLink = styled.a`
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const EyeIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text-secondary);
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  width: 800px;
  height: 800px;
  border-radius: 50%;
  background-color: var(--color-primary);
  filter: blur(250px);
  opacity: 0.07;
  top: -300px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 0;
`;

export const ResetPassword: React.FC = () => {
  usePageTitle('Сброс пароля');
  
  const [formData, setFormData] = useState({
    telegram: '',
    ottCode: '',
    newPassword: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const { executeApiCall } = useApiCall();
  const { isAuthenticated } = useAuthStore();
  const { showSuccess } = useToast();
  const location = useLocation();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.telegram.trim()) {
      return;
    }
    
    if (!formData.ottCode.trim()) {
      return;
    }
    
    if (!formData.newPassword.trim()) {
      return;
    }
    
    const result = await executeApiCall(
      () => api.resetPassword({
        telegram: formData.telegram.trim(),
        ott_code: formData.ottCode.trim(),
        new_password: formData.newPassword
      }),
      {
        successMessage: 'Пароль успешно изменен',
        errorMessage: 'Ошибка при сбросе пароля'
      }
    );
    
    if (result !== null) {
      setFormData({
        telegram: '',
        ottCode: '',
        newPassword: '',
      });
    }
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const renderEyeIcon = (show: boolean, onClick: () => void) => (
    <EyeIcon onClick={onClick}>
      {show ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        </svg>
      )}
    </EyeIcon>
  );
  
  if (isAuthenticated) {
    return <Navigate to="/statistics" replace />;
  }
  
  return (
    <ResetContainer>
      <BackgroundGlow />
      <ResetCard>
        <ResetHeader>
          <Logo />
          <div>
            <ResetTitle>Сброс пароля</ResetTitle>
            <ResetSubtitle>
              Введите ваш Telegram, код подтверждения и новый пароль
            </ResetSubtitle>
          </div>
        </ResetHeader>
        
        <InfoBox>
          <InfoText>
            Код для сброса пароля можно получить в telegram боте
          </InfoText>
          <BotLink href="https://t.me/lf_safe_search_verify_bot" target="_blank" rel="noopener noreferrer">
            @lf_safe_search_verify_bot
          </BotLink>
        </InfoBox>
        
        <Form onSubmit={handleSubmit}>
          <FormField>
            <Input
              label="Telegram"
              name="telegram"
              type="text"
              value={formData.telegram}
              onChange={handleChange}
              placeholder="@username или ссылка"
              required
            />
          </FormField>
          
          <FormField>
            <Input
              label="Код подтверждения"
              name="ottCode"
              type="text"
              value={formData.ottCode}
              onChange={handleChange}
              placeholder="Введите код"
              required
            />
          </FormField>
          
          <FormField>
            <Input
              label="Новый пароль"
              name="newPassword"
              type={showPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Введите новый пароль"
              icon={renderEyeIcon(showPassword, togglePasswordVisibility)}
              iconPosition="right"
              onIconClick={togglePasswordVisibility}
              required
            />
          </FormField>
          
          <FormActions>
            <Button
              type="submit"
              variant="primary"
              disabled={!formData.telegram.trim() || !formData.ottCode.trim() || !formData.newPassword.trim()}
            >
              Сбросить пароль
            </Button>
          </FormActions>
        </Form>
        
        <BackToLogin to="/login">
          ← Вернуться к входу
        </BackToLogin>
      </ResetCard>
    </ResetContainer>
  );
};

export default ResetPassword; 